import { type ComponentType, Suspense, useCallback } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import qs from "query-string";

import { PrivateRoute } from "@src/components/routing";
import { AgentExperience } from "@src/containers/app/agentExperience";
import { MaterialUiProviders } from "@src/mui/providers/MaterialUiProviders";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import { pseudo } from "@src/modules/session";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { ProtectedAgentCasesPage } from "@src/appV2/ExperimentalHomeHealth/AgentCases/ProtectedAgentCasesPage";
import { ProtectedAgentVisitsPage } from "@src/appV2/ExperimentalHomeHealth/AgentVisits/ProtectedAgentVisitsPage";
import { HomeHealthProtectedCasesPage } from "@src/appV2/ExperimentalHomeHealth/Cases/ProtectedCasesPage";
import { HomeHealthProtectedVisitVerificationPage } from "@src/appV2/ExperimentalHomeHealth/VisitVerification/ProtectedVisitVerificationPage";
import { ProtectedMobileVisitVerificationPage } from "@src/appV2/redesign/HomeHealth/VisitVerification/ProtectedMobileVisitVerificationPage";
import { AppV2Routes } from "@src/appV2/App/Routes";
import { loadScheduleRouteModule } from "@src/appV2/redesign/DailyView/preloadSchedule";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";

import { useAdminMenuPrivilegesProviderContext } from "../privilegesProviders/AdminMenuPrivilegesProvider";
import { useWorkplaceMenuPrivilegesProviderContext } from "../privilegesProviders/WorkplaceMenuPrivilegesProvider";
import { useWorkerMenuPrivilegesProviderContext } from "../privilegesProviders/WorkerMenuPrivilegesProvider";
import { LegacyRouteWrapper, type LegacyRouteWrapperProps } from "./LegacyRouteWrapper";

// ---------------------------------------------------------------------------
// withLegacyWrapper HOC — wraps a component in LegacyRouteWrapper (legacy MUI
// theme + padding) so each route can be a direct <Switch> child.
// ---------------------------------------------------------------------------
interface LegacyWrapperOptions {
  rootSx?: LegacyRouteWrapperProps["rootSx"];
}

function withLegacyWrapper<P extends object>(
  Component: ComponentType<P>,
  options: LegacyWrapperOptions = {}
): ComponentType<P> {
  function LegacyWrapped(props: P) {
    return (
      <LegacyRouteWrapper rootSx={options.rootSx}>
        <Component {...props} />
      </LegacyRouteWrapper>
    );
  }
  LegacyWrapped.displayName = `LegacyWrapped(${
    Component.displayName || Component.name || "Component"
  })`;
  return LegacyWrapped;
}

// ---------------------------------------------------------------------------
// Public lazy components
// ---------------------------------------------------------------------------
const ReferenceForm = loadLazily(
  async () =>
    await import("@src/containers/referenceForm").then((module) => ({
      default: module.ReferenceForm,
    }))
);
const HomeHealthCasesPage = withLegacyWrapper(HomeHealthProtectedCasesPage);
const HomeHealthVisitVerificationPage = withLegacyWrapper(HomeHealthProtectedVisitVerificationPage);

const ShiftDetailsPage = loadLazily(
  async () =>
    await import("@src/components/ShiftDetails/ShiftDetailsPage").then((module) => ({
      default: module.ShiftDetailsPage,
    }))
);

const WorkerLanding = loadLazily(async () => await import("@src/containers/workerLanding"));

const ShiftSignoutFeedbackForm = loadLazily(
  async () =>
    await import("@src/containers/ShiftSignoutFlow/shiftSignoutFlowForm").then((module) => ({
      default: module.ShiftSignoutFlowForm,
    }))
);

const PublicWorkplacePlacementsPage = loadLazily(
  async () =>
    await import("@src/appV2/PublicWorkplacePlacements/Page").then((module) => ({
      default: module.PublicWorkplacePlacementsPage,
    }))
);

const AppDownloadRedirect = loadLazily(
  async () =>
    await import("@src/appV2/redesign/AppDownload/AppDownloadRedirect").then((module) => ({
      default: module.AppDownloadRedirect,
    }))
);

// ---------------------------------------------------------------------------
// Admin lazy components (pseudo-login helpers)
// ---------------------------------------------------------------------------
const AdminCalendar = loadLazily(
  async () =>
    await import("@src/containers/calendar").then((module) => ({
      default: module.AdminCalendar,
    }))
);

// ---------------------------------------------------------------------------
// Workplace lazy components — non-legacy (no LegacyRouteWrapper needed)
// ---------------------------------------------------------------------------
const LazyDailyView = loadLazily(
  async () =>
    await loadScheduleRouteModule().then((module) => ({
      default: module.DailyView,
    }))
);

const RedesignedChatPage = loadLazily(
  async () =>
    await import("@src/appV2/redesign/Chat/page/ChatPageContainer").then((module) => ({
      default: module.ChatPageContainer,
    }))
);

const SmsPortalPage = loadLazily(
  async () =>
    await import("@src/appV2/redesign/SmsPortal/page/SmsPortalPageContainer").then((module) => ({
      default: module.SmsPortalPageContainer,
    }))
);

const RedesignedSettingsPage = loadLazily(
  async () =>
    await import("@src/appV2/redesign/Settings/SettingsPage").then((module) => ({
      default: module.SettingsPage,
    }))
);

const RedesignedMyAccountPage = loadLazily(
  async () =>
    await import("@src/appV2/redesign/MyAccount/MyAccountPage").then((module) => ({
      default: module.MyAccountPage,
    }))
);

const CoverageMapPage = loadLazily(
  async () =>
    await import("@src/appV2/redesign/HomeHealth/CoverageMap/CoverageMap").then((module) => ({
      default: module.CoverageMap,
    }))
);

const ConnectEmrPage = loadLazily(
  async () =>
    await import("@src/appV2/redesign/HomeHealth/ConnectEmr/ConnectEmr").then((module) => ({
      default: module.ConnectEmr,
    }))
);

// ---------------------------------------------------------------------------
// Workplace lazy components — legacy (wrapped with LegacyRouteWrapper)
// ---------------------------------------------------------------------------
const GettingStartedPageWrapper = loadLazily(
  async () =>
    await import("@src/appV2/redesign/GettingStarted/GettingStartedPage").then((module) => ({
      default: module.GettingStartedPage,
    }))
);

// Not wrapped in withLegacyWrapper: the page hosts worker-profile side panels
// via SidePanelPageShell, which provides the legacy providers/padding itself.
const FacilityDashboard = loadLazily(
  async () =>
    await import("@src/containers/facilityDashboard").then((module) => ({
      default: module.FacilityDashboard,
    }))
);

const MyProfessionalsPageWrapper = loadLazily(
  async () =>
    await import("@src/containers/workers/Professionals/MyProfessionals/MyProfessionalsPageWrapper").then(
      (module) => ({
        default: module.MyProfessionalsPageWrapper,
      })
    )
);

const WorkersDirectory = loadLazily(
  async () =>
    await import("@src/containers/workplace/WorkersDirectory/WorkersDirectory").then((module) => ({
      default: module.WorkersDirectoryPage,
    }))
);

const WorkersDirectoryWorkerDetails = loadLazily(
  async () =>
    await import("@src/containers/workplace/WorkerDetails/WorkerDetailsPageWrapper").then(
      (module) => ({
        default: module.WorkerDetailsPageWrapper,
      })
    )
);

const WorkplaceWorkerDetails = loadLazily(
  async () =>
    await import("@src/containers/workers/WorkerDetails/WorkerDetailsPageWrapper").then(
      (module) => ({
        default: module.WorkerDetailsPageWrapper,
      })
    )
);

const PlacementCandidateDetailsPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/PlacementCandidates/PlacementCandidateDetailsPage").then(
        (module) => ({
          default: module.PlacementCandidateDetailsPage,
        })
      )
  )
);

const WorkplaceSignContract = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/workplaceSignContract/WorkplaceSignContract").then(
        (module) => ({
          default: module.WorkplaceSignContractPage,
        })
      )
  )
);

const WorkplaceBlockShifts = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/workplaceBlockShifts/WorkplaceBlockShifts").then((module) => ({
        default: module.WorkplaceBlockShifts,
      }))
  )
);

const FacilityWorkerInstructions = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/facilityWorkerInstructions").then((module) => ({
        default: module.FacilityWorkerInstructions,
      }))
  )
);

const FacilityRulesPageWrapper = loadLazily(
  async () =>
    await import("@src/appV2/redesign/WorkplaceRulesQuiz/WorkplaceRulesQuizPage").then(
      (module) => ({
        default: module.WorkplaceRulesQuizPage,
      })
    )
);

const InterviewSchedulePage = loadLazily(
  async () =>
    await import("@src/appV2/InterviewSchedule/Page").then((module) => ({
      default: module.InterviewSchedulePage,
    }))
);

const UnverifiedPlacementCandidateApplicationPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/UnverifiedPlacementCandidateApplication/UnverifiedPlacementCandidateApplication").then(
        (module) => ({
          default: module.UnverifiedPlacementCandidateApplicationPage,
        })
      )
  )
);

const WorkplaceWorkerDocuments = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/WorkerDocuments/WorkerDocumentsView").then((module) => ({
        default: module.WorkerDocumentsView,
      }))
  )
);

const WorkplaceTimesheetView = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/accountManagement/invoice/timesheetView").then((module) => ({
        default: module.TimesheetView,
      }))
  )
);

const JobsLandingPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/JobsLandingPage/Page").then((module) => ({
        default: module.JobsLandingPage,
      }))
  )
);

const ManageJobsTabs = loadLazily(
  async () =>
    await import("@src/appV2/ManageJobs/ManageJobsTabs").then((module) => ({
      default: module.ManageJobsTabs,
    }))
);

const PlacementCandidates = loadLazily(
  async () =>
    await import("@src/appV2/PlacementCandidates/Page").then((module) => ({
      default: module.PlacementCandidatesPage,
    }))
);

const InterviewSettings = loadLazily(
  async () =>
    await import("@src/appV2/InterviewSettings/Page").then((module) => ({
      default: module.InterviewSettingsPage,
    }))
);

const WorkplaceChartingPageWrapper = loadLazily(
  async () =>
    await import("@src/appV2/redesign/Charting/ChartingPage").then((module) => ({
      default: module.ChartingPage,
    }))
);

const AnalyticsDashboard = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/Analytics/components/AnalyticsDashboard").then((module) => ({
        default: module.AnalyticsDashboard,
      }))
  )
);

const SpendingControlsPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/Corporate/SpendingControls/Page").then((module) => ({
        default: module.SpendingControlsPage,
      }))
  )
);

const WorkplaceAnalyticsPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/redesign/WorkplaceAnalytics/WorkplaceAnalyticsPage").then(
        (module) => ({
          default: module.WorkplaceAnalyticsPage,
        })
      )
  )
);

const WorkplaceSpendingControlsPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/redesign/SpendingControls/WorkplaceSpendingControls/WorkplaceSpendingControls").then(
        (module) => ({
          default: module.WorkplaceSpendingControls,
        })
      )
  )
);

const LiveWorkerEtaPage = loadLazily(
  async () =>
    await import("@src/appV2/redesign/LiveWorkerEta/LiveWorkerEtaPage").then((module) => ({
      default: module.LiveWorkerEtaPage,
    }))
);

const TeamMembersPageWrapper = loadLazily(
  async () =>
    await import("@src/containers/teamMembers/TeamMembersPageWrapper").then((module) => ({
      default: module.TeamMembersPageWrapper,
    }))
);

const PrnRosterPage = loadLazily(
  async () =>
    await import("@src/appV2/redesign/PrnRoster/Page").then((module) => ({
      default: module.PrnRosterPage,
    }))
);

const WorkplaceRequirementsPage = loadLazily(
  async () =>
    await import("@src/appV2/redesign/RequiredDocuments/WorkplaceRequirementsContainer").then(
      (module) => ({
        default: module.WorkplaceRequirementsContainer,
      })
    )
);

const FacilityUpcomingChargesV2 = loadLazily(
  async () =>
    await import("@src/appV2/redesign/UpcomingCharges/UpcomingChargesPage").then((module) => ({
      default: module.UpcomingChargesPage,
    }))
);

const FacilityInvoiceHistory = loadLazily(
  async () =>
    await import("@src/appV2/redesign/BillingHistory/BillingHistoryPage").then((module) => ({
      default: module.BillingHistoryPage,
    }))
);

const FacilityReporting = loadLazily(
  async () =>
    await import("@src/appV2/redesign/Reporting/Page").then((module) => ({
      default: module.ReportingPage,
    }))
);

const ContractTerms = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/contractTerms").then((module) => ({
        default: module.ContractTerms,
      }))
  )
);

const WorkplaceReferAndEarn = loadLazily(
  async () =>
    await import("@src/appV2/redesign/ReferAndEarn/ReferAndEarnPage").then((module) => ({
      default: module.ReferAndEarnPage,
    }))
);

const FacilityInvoiceDispute = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/FacilityInvoiceDispute").then((module) => ({
        default: module.FacilityInvoiceDisputeContainer,
      }))
  )
);

const FacilityInvoiceDisputeRedirect = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/FacilityInvoiceDispute/FacilityInvoiceDisputeRedirect").then(
        (module) => ({
          default: module.FacilityInvoiceDisputeRedirect,
        })
      )
  )
);

const WorkplaceTimesheetViewRedirect = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/accountManagement/invoice/timesheetViewRedirect").then(
        (module) => ({
          default: module.TimesheetViewRedirect,
        })
      )
  )
);

const HelpCenterPageWrapper = loadLazily(
  async () =>
    await import("@src/containers/helpCenter/HelpCenterPageWrapper").then((module) => ({
      default: module.HelpCenterPageWrapper,
    }))
);

// ---------------------------------------------------------------------------
// Worker lazy components (no legacy wrapper needed — worker routes don't use it)
// ---------------------------------------------------------------------------
const AgentDashboard = loadLazily(() =>
  import("@src/containers/agentDashboard").then((module) => ({
    default: module.AgentDashboard,
  }))
);

const WorkerDnrPage = loadLazily(() =>
  import("@src/appV2/redesign/DNR/Page").then((module) => ({
    default: module.WorkerDnrPage,
  }))
);

const Payroll = withLegacyWrapper(
  loadLazily(async () => await import("@src/containers/payroll/payrollView"))
);

const StripeTransfers = withLegacyWrapper(
  loadLazily(async () => await import("@src/containers/payroll/stripeTransfers/stripeTransfers"))
);

const StripePayouts = withLegacyWrapper(
  loadLazily(async () => await import("@src/containers/payroll/stripePayouts/stripePayouts"))
);

const AttendanceScore = withLegacyWrapper(
  loadLazily(async () => await import("@src/containers/attendanceScore"))
);

const AdminPlacementCandidatePage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/PlacementCandidates/AdminPlacementCandidatePage").then((module) => ({
        default: module.AdminPlacementCandidatePage,
      }))
  )
);

const AgentExclusionsPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/PlacementCandidates/PlacementCandidateExclusionsPage").then(
        (module) => ({
          default: module.PlacementCandidateExclusionsPage,
        })
      )
  )
);

const WorkerEditProfile = withLegacyWrapper(
  loadLazily(async () => await import("@src/containers/editProfile"))
);

// ---------------------------------------------------------------------------
// Admin lazy components — legacy (wrapped with LegacyRouteWrapper)
// ---------------------------------------------------------------------------
const Accounts = withLegacyWrapper(
  loadLazily(async () => await import("@src/containers/accountManagement"))
);

const ShiftVerificationLandingPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/ShiftVerification/LandingPage").then((module) => ({
        default: module.default,
      }))
  )
);

const ShiftVerificationPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/ShiftVerification/ShiftVerificationPage").then((module) => ({
        default: module.default,
      }))
  )
);

const FacilityPreferencesPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/ShiftVerification/FacilityPreferencesPage").then((module) => ({
        default: module.default,
      }))
  )
);

const ShiftCreatePage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/ShiftVerification/ShiftCreatePage").then((module) => ({
        default: module.default,
      }))
  )
);

const ExtrasPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/ShiftVerification/ExtrasPage").then((module) => ({
        default: module.default,
      }))
  )
);

const ManualShiftVerificationPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/ShiftVerification/ManualShiftVerificationPage").then((module) => ({
        default: module.default,
      }))
  )
);

const ShiftSearchPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/adminShifts/ShiftSearchPage").then((module) => ({
        default: module.ShiftSearchPage,
      }))
  )
);

const Logs = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/logs").then((module) => ({
        default: module.Logs,
      }))
  )
);

const TimeclockRequests = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/timeclockrequests").then((module) => ({
        default: module.TimeclockRequests,
      }))
  )
);

const UrgentShiftsNoShowCases = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/urgentShiftsNoShowCases").then((module) => ({
        default: module.UrgentShiftsNoShowCases,
      }))
  )
);

const ProofOfEarnings = withLegacyWrapper(
  loadLazily(async () => await import("@src/containers/proofOfEarnings"))
);

const PayRates = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/shiftPayments/payRates").then((module) => ({
        default: module.PayRates,
      }))
  )
);

const ShiftPayments = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/shiftPayments").then((module) => ({
        default: module.ShiftPayments,
      }))
  )
);

const DownloadICAgreement = withLegacyWrapper(
  loadLazily(async () => await import("@src/containers/icAgreement/downloadIcAgreement"))
);

const AdminToolsTimekeepingVideosPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/AdminTools/Page").then((module) => ({
        default: module.AdminToolsPage,
      }))
  )
);

const AdminToolsShiftExplainerPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/AdminTools/ShiftExplainer/components/Page").then((module) => ({
        default: module.ShiftExplainerPage,
      }))
  )
);

const PlacementScrapingPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/PlacementScraping/Page").then((module) => ({
        default: module.PlacementScrapingPage,
      }))
  )
);

const BadgeVerificationGradingPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/AdminTools/BadgeVerificationGrading/Page").then((module) => ({
        default: module.BadgeVerificationGradingPage,
      }))
  )
);

const SsnWorkerLookupPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/AdminTools/SsnWorkerLookup/components/Page").then((module) => ({
        default: module.SsnWorkerLookupPage,
      }))
  )
);

const WorkerEvidencesPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/redesign/Evidence/Page").then((module) => ({
        default: module.WorkerEvidencesPage,
      }))
  )
);

const HomeHealthWorkerLookupPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/ExperimentalHomeHealth/WorkerView/components/HomeHealthWorkerLookupPage").then(
        (module) => ({
          default: module.HomeHealthWorkerLookupPage,
        })
      )
  )
);

const ManageQualifications = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/Admin").then((module) => ({
        default: module.ManageQualificationsPage,
      }))
  )
);

const ManageWorkplaceTypes = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/Admin").then((module) => ({
        default: module.ManageWorkplaceTypesPage,
      }))
  )
);

const RequirementV2 = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/redesign/RequirementV2/Page").then((module) => ({
        default: module.RequirementV2PageWrapper,
      }))
  )
);

const AdminPlacementsAvailabilityPage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/AdminPlacements/Availability/Page").then((module) => ({
        default: module.AdminPlacementsAvailabilityPage,
      }))
  )
);

const AdminQRCode = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/QRCode/QRCodePage").then((module) => ({
        default: module.QRCodePage,
      }))
  )
);

const AdminWorkerDocuments = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/WorkerDocuments/WorkerDocumentsView").then((module) => ({
        default: module.WorkerDocumentsView,
      }))
  )
);

const AdminDocuments = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/WorkerDocuments/WorkerDocumentsView").then((module) => ({
        default: module.WorkerDocumentsView,
      }))
  )
);

const InvoiceView = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/components/invoiceView").then((module) => ({
        default: module.InvoiceView,
      }))
  )
);

const AdminPDFGenerator = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/accountManagement/invoice/pdfGenerator").then((module) => ({
        default: module.PDFGenerator,
      }))
  )
);

const AdminPostInvoiceDisputes = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminPostInvoiceDisputes").then(
        (module) => ({
          default: module.AdminPostInvoiceDisputes,
        })
      )
  )
);

const AdminPreInvoiceDisputes = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminPreInvoiceDisputes").then(
        (module) => ({
          default: module.AdminPreInvoiceDisputes,
        })
      )
  )
);

const AdminDisputePage = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminDisputePage").then(
        (module) => ({
          default: module.AdminDisputePage,
        })
      )
  )
);

const AdminPreInvoiceDisputeResolution = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminPreInvoiceDisputeResolution").then(
        (module) => ({
          default: module.AdminPreInvoiceDisputeResolution,
        })
      )
  )
);

const AdminTimesheetView = withLegacyWrapper(
  loadLazily(
    async () =>
      await import("@src/containers/accountManagement/invoice/timesheetView").then((module) => ({
        default: module.TimesheetView,
      }))
  )
);

// ---------------------------------------------------------------------------
// Helper components (moved from AdminRoutes.tsx)
// ---------------------------------------------------------------------------

function ConnectedAdminCalendar() {
  const dispatch = useDispatch();
  const boundPseudo = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => dispatch(pseudo(data)),
    [dispatch]
  );
  return <AdminCalendar pseudo={boundPseudo} />;
}

const LegacyConnectedAdminCalendar = withLegacyWrapper(ConnectedAdminCalendar);

// ---------------------------------------------------------------------------
// AllRoutes
// ---------------------------------------------------------------------------

export interface AllRoutesProps {
  admin: boolean;
  isAdmin: boolean;
  isWorker: boolean;
  isWorkplace: boolean;
  renderStartPage: () => JSX.Element | null;
}

export function AllRoutes(props: AllRoutesProps) {
  const { admin, isAdmin, isWorker, isWorkplace, renderStartPage } = props;

  const hasSession = isAdmin || isWorker || isWorkplace;

  // Privilege contexts — safe to call unconditionally (providers always wrap in NavigationMenuContainer)
  const workplaceMenuPrivileges = useWorkplaceMenuPrivilegesProviderContext();
  const workerMenuPrivileges = useWorkerMenuPrivilegesProviderContext();
  const adminMenuPrivileges = useAdminMenuPrivilegesProviderContext();

  // Worker mobile-app redirect logic (from WorkerRoutes.tsx)
  const { admin: sessionAdmin, type, userId: sessionUserId } = useSession();
  const isWorkerSession = type === "AGENT";
  const shouldRedirectToMobileApp = !sessionAdmin && isWorkerSession;

  const isMobile = useIsMobile();
  const isHomeHealthEnabled = useIsHomeHealthEnabled();

  const location = useLocation();
  const urlUserId =
    typeof qs.parse(location.search).userId === "string"
      ? (qs.parse(location.search).userId as string)
      : undefined;

  // If user has no session (logged out), redirect to login page
  // This prevents users from getting stuck on protected routes after logout
  if (!hasSession) {
    return <Redirect to="/login" />;
  }

  // Session-aware catch-all target. Workplace users default to Schedule when
  // they have daily-view access; otherwise the legacy facility dashboard.
  const workplaceRedirect = workplaceMenuPrivileges.dailyView
    ? routes.schedule
    : routes.facilityDashboard;
  // eslint-disable-next-line no-nested-ternary
  const catchAllRedirect = isAdmin
    ? routes.adminAccountsAgents
    : isWorker
      ? routes.agentDashboard
      : workplaceRedirect;

  return (
    <Suspense fallback={<></>}>
      <Switch>
        {/* ================================================================
            1. Start pages
            ================================================================ */}
        <Route exact path="/" render={renderStartPage} />
        <Route exact path="/enter" render={renderStartPage} />

        {/* ================================================================
            2. Public routes — accessible regardless of session type
            ================================================================ */}
        <Route path={routes.reference} component={ReferenceForm} />
        <Route exact path={routes.shiftDetails} component={ShiftDetailsPage} />
        <Route exact path={routes.workers} component={WorkerLanding} />
        <Route path={routes.shiftSignoutFeedback} component={ShiftSignoutFeedbackForm} />
        <Route
          exact
          path={routes.publicWorkplacePlacements}
          component={PublicWorkplacePlacementsPage}
        />
        <Route exact path={routes.appDownload} component={AppDownloadRedirect} />

        {/* ================================================================
            3. /agent/dashboard — single route, branches by session type.
               Matches the legacy appLayout.tsx pattern (lines 862-873):
               - Real worker (isWorker && !admin) → AgentExperience (mobile redirect)
               - Admin pseudo-as-worker (isWorker && admin) → AgentDashboard
               - Not a worker → redirect to /calendar with query params for pseudo-login
            ================================================================ */}
        <Route
          exact
          path={routes.agentDashboard}
          render={() => {
            if (isWorker && shouldRedirectToMobileApp) {
              return <AgentExperience />;
            }
            if (isWorker) {
              // If the URL contains a userId that differs from the active session,
              // the admin is switching to a different worker. Re-run pseudo-login
              // via /calendar so the session is refreshed before rendering the dashboard.
              if (urlUserId !== undefined && urlUserId !== sessionUserId) {
                return <Redirect to={`${routes.calendar}${location.search}`} />;
              }
              return <AgentDashboard />;
            }
            // Not a worker — redirect to /calendar to initiate pseudo-login
            return <Redirect to={`${routes.calendar}${location.search}`} />;
          }}
        />

        {/* Agent Home Health routes — CBH employees pseudo-logged in as worker */}
        {admin && isWorker && (
          <PrivateRoute
            exact
            path={routes.agentCases}
            component={withLegacyWrapper(ProtectedAgentCasesPage)}
            isAuthorized={admin && isWorker}
          />
        )}
        {admin && isWorker && (
          <PrivateRoute
            exact
            path={routes.agentVisits}
            component={withLegacyWrapper(ProtectedAgentVisitsPage)}
            isAuthorized={admin && isWorker}
          />
        )}

        {/* /calendar — pseudo-login endpoint: calls pseudo({ userId }) then → /enter */}
        {admin && (
          <PrivateRoute
            exact
            path={routes.calendar}
            component={LegacyConnectedAdminCalendar}
            isAuthorized={adminMenuPrivileges.routeAuth?.isAdmin}
          />
        )}

        {/* ================================================================
            4. /facility/dashboard — pseudo-login guard (mirrors /agent/dashboard).
               Rendered only when a redirect to /calendar is needed:
               - Not a workplace session (admin opening a facility URL directly)
               - Workplace session but URL userId differs from session (switching facilities)
               When neither applies, this route is absent and the gated PrivateRoute
               below handles rendering normally.
            ================================================================ */}
        {isAdmin && (
          <Route
            path={routes.facilityDashboard}
            render={() => <Redirect to={routes.adminAccountsAgents} />}
          />
        )}
        {(!isWorkplace || (urlUserId !== undefined && urlUserId !== sessionUserId)) && (
          <Route
            path={routes.facilityDashboard}
            render={() => <Redirect to={`${routes.calendar}${location.search}`} />}
          />
        )}

        {/* ================================================================
            5. Workplace routes — gated by isWorkplace
            ================================================================ */}

        {/* Home Health routes — wrapped in LegacyRouteWrapper because these
            components were built against the old MUI theme (Inter Tight) */}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.homeHealthCases}
            component={HomeHealthCasesPage}
            isAuthorized={isWorkplace || admin}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.homeHealthVisitVerificationMobile}
            component={ProtectedMobileVisitVerificationPage}
            isAuthorized={isWorkplace || admin}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.homeHealthVisitVerification}
            component={HomeHealthVisitVerificationPage}
            isAuthorized={isWorkplace || admin}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.homeHealthCoverageMap}
            component={CoverageMapPage}
            isAuthorized={isWorkplace || admin}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.homeHealthConnectEmr}
            component={ConnectEmrPage}
            isAuthorized={isWorkplace || admin}
          />
        )}

        {/* AppV2 routes (public auth routes like /v2/email-login-link) */}
        {isWorkplace && (
          <Route path={routes.appV2}>
            <MaterialUiProviders>
              <AppV2Routes />
            </MaterialUiProviders>
          </Route>
        )}

        {/* Redesigned components without Legacy Wrapper */}
        {isWorkplace && (
          <Route
            exact
            path={routes.dailyView}
            render={({ location: routeLocation }) => (
              <Redirect
                to={{
                  pathname: routes.schedule,
                  search: routeLocation.search,
                  hash: routeLocation.hash,
                }}
              />
            )}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            path={routes.schedule}
            component={LazyDailyView}
            isAuthorized={workplaceMenuPrivileges.dailyView}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            path={routes.chatWithConversation}
            component={RedesignedChatPage}
            isAuthorized={workplaceMenuPrivileges.chatRedesign}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.settings}
            component={RedesignedSettingsPage}
            isAuthorized={workplaceMenuPrivileges.managementSection?.settings}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            // Also matches /my-account/passkeys so the URL-driven passkey
            // management dialog renders on the My Account page (DEVOP-5458).
            path={[routes.myAccount, routes.myAccountPasskeys]}
            component={RedesignedMyAccountPage}
            isAuthorized={workplaceMenuPrivileges.managementSection?.myAccount}
          />
        )}

        {/* Legacy workplace routes */}
        {/* When the redesigned Schedule tab is enabled, /facility/dashboard
            redirects to Schedule (daily view by default). The legacy
            classic calendar UI lives at /calendar-classic and is reachable
            from the sidebar's "Calendar (Classic)" entry. This stops
            bookmarks/autocomplete from passively pinning users to the
            legacy view. */}
        {isWorkplace && (isHomeHealthEnabled || workplaceMenuPrivileges.dailyView) && (
          <Route
            exact
            path={routes.facilityDashboard}
            render={({ location: routeLocation }) => {
              const params = new URLSearchParams(routeLocation.search);

              // Home Health workplaces' home is the cases list. The
              // previous `!isMobile` gate fell HH-on-mobile through to
              // /schedule, which HH non-admins cannot access (dailyView
              // privilege stripped by HOME_HEALTH_MENU_ITEMS_HIDDEN),
              // producing an infinite redirect loop on Capacitor and
              // narrow viewports (TECH-1370). Route HH to Cases on every
              // viewport; mobile UX polish on Cases is tracked separately.
              if (isHomeHealthEnabled) {
                return <Redirect to={{ pathname: routes.homeHealthCases }} />;
              }

              // On desktop, until ORB-1848 ports the shift-block management
              // dialog into the redesign Schedule, route shiftblockId
              // deep-links to the legacy classic calendar so backend email
              // links keep working. On mobile, /calendar-classic is hidden
              // from navigation, so just fall through to /schedule.
              if (!isMobile && params.has("shiftblockId")) {
                return (
                  <Redirect
                    to={{
                      pathname: routes.calendarClassic,
                      search: routeLocation.search,
                    }}
                  />
                );
              }

              // Normalize the cbh.care /postShiftBlock short link's casing
              // (`actionType=postShiftBlock`) to the value the redesign
              // Monthly view consumes (`POST_SHIFT_BLOCK`).
              if (params.get("actionType") === "postShiftBlock") {
                params.set("actionType", "POST_SHIFT_BLOCK");
              }

              const search = params.toString() ? `?${params.toString()}` : undefined;
              return <Redirect to={{ pathname: routes.schedule, search }} />;
            }}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            path={routes.facilityDashboard}
            component={FacilityDashboard}
            isAuthorized={workplaceMenuPrivileges.calendar}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            path={routes.calendarClassic}
            component={FacilityDashboard}
            isAuthorized={workplaceMenuPrivileges.calendar}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.facilityOnboarding}
            component={GettingStartedPageWrapper}
            isAuthorized={workplaceMenuPrivileges.workplaceOnboarding}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceWorkerDetails}
            component={WorkplaceWorkerDetails}
            isAuthorized={workplaceMenuPrivileges.routeAuth?.isWorkplaceOrAdmin}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceWorkers}
            component={MyProfessionalsPageWrapper}
            isAuthorized={workplaceMenuPrivileges.professionalsSection?.myProfessionals}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workersDirectoryWorkerDetails}
            component={WorkersDirectoryWorkerDetails}
            isAuthorized={workplaceMenuPrivileges.routeAuth?.hasPermissionToAccessWorkerDirectory}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workersDirectory}
            component={WorkersDirectory}
            isAuthorized={workplaceMenuPrivileges.professionalsSection?.browseProfessionals}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.document}
            component={WorkplaceWorkerDocuments}
            isAuthorized={workplaceMenuPrivileges.routeAuth?.isDocumentAuthorized}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceBlockShifts}
            component={WorkplaceBlockShifts}
            isAuthorized={workplaceMenuPrivileges.routeAuth?.hasPermissionToAccessWorkerDirectory}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.jobsLandingPage}
            component={JobsLandingPage}
            isAuthorized={workplaceMenuPrivileges.permanentHiringSection?.placementsLandingPage}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.manageJobs}
            component={ManageJobsTabs}
            isAuthorized={workplaceMenuPrivileges.permanentHiringSection?.applicationAndJobs}
          />
        )}
        <Route
          exact
          path={routes.workplaceHireProfessionalDetails}
          component={PlacementCandidateDetailsPage}
        />
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceHireProfessionals}
            component={PlacementCandidates}
            isAuthorized={workplaceMenuPrivileges.permanentHiringSection?.browseTalentPool}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.interviewSettings}
            component={InterviewSettings}
            isAuthorized={workplaceMenuPrivileges.permanentHiringSection?.interviewAvailability}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.interviewSchedule}
            component={InterviewSchedulePage}
            isAuthorized={workplaceMenuPrivileges.permanentHiringSection?.interviewAvailability}
          />
        )}
        <Route
          exact
          path={routes.workplaceJobApplicantDetails}
          component={UnverifiedPlacementCandidateApplicationPage}
        />
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceCharting}
            component={WorkplaceChartingPageWrapper}
            isAuthorized={workplaceMenuPrivileges.charting}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceAnalytics}
            component={AnalyticsDashboard}
            isAuthorized={workplaceMenuPrivileges.analyticsSection}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.corporateSpendingControls}
            component={SpendingControlsPage}
            isAuthorized={workplaceMenuPrivileges.corporateCenterSection?.spendingControls}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceAnalyticsCorporate}
            component={WorkplaceAnalyticsPage}
            isAuthorized={workplaceMenuPrivileges.corporateCenterSection?.nativeAnalytics}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceSpendingControls}
            component={WorkplaceSpendingControlsPage}
            isAuthorized={workplaceMenuPrivileges.routeAuth?.workplaceSpendingControls}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.facilityLiveWorkerETA}
            component={LiveWorkerEtaPage}
            isAuthorized={workplaceMenuPrivileges.routeAuth?.hasDefaultWorkplacePermissions}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceUsers}
            component={TeamMembersPageWrapper}
            isAuthorized={workplaceMenuPrivileges.managementSection?.teamMembers}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceInternalRoster}
            component={PrnRosterPage}
            isAuthorized={workplaceMenuPrivileges.managementSection?.internalRoster}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceDocumentMapping}
            component={WorkplaceRequirementsPage}
            isAuthorized={workplaceMenuPrivileges.managementSection?.requiredDocuments}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.facilityInvoiceReporting}
            component={FacilityReporting}
            isAuthorized={workplaceMenuPrivileges.billingSection?.reporting}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.facilityInvoiceUpcomingCharges}
            component={FacilityUpcomingChargesV2}
            isAuthorized={workplaceMenuPrivileges.billingSection?.upcomingCharges}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.facilityInvoiceHistory}
            component={FacilityInvoiceHistory}
            isAuthorized={workplaceMenuPrivileges.billingSection?.invoiceHistory}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.facilityInvoiceContractTerms}
            component={ContractTerms}
            isAuthorized={workplaceMenuPrivileges.billingSection?.contractTerms}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.helpCenter}
            component={HelpCenterPageWrapper}
            isAuthorized={workplaceMenuPrivileges.helpCenter}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceReferAndEarn}
            component={WorkplaceReferAndEarn}
            isAuthorized={workplaceMenuPrivileges.referAndEarn}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.workplaceSignContract}
            component={WorkplaceSignContract}
            isAuthorized={workplaceMenuPrivileges.calendar}
          />
        )}
        <Route
          exact
          path={routes.facilityWorkerInstructions}
          component={
            workplaceMenuPrivileges.workplaceRulesQuiz
              ? FacilityRulesPageWrapper
              : FacilityWorkerInstructions
          }
        />
        {!admin && <Route exact path={routes.timesheet} component={WorkplaceTimesheetView} />}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.facilityInvoiceDispute}
            component={FacilityInvoiceDispute}
            isAuthorized={workplaceMenuPrivileges.routeAuth?.isFacilityInvoiceDisputeAuthorized}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.facilityInvoiceDisputeRedirect}
            component={FacilityInvoiceDisputeRedirect}
            isAuthorized={workplaceMenuPrivileges.routeAuth?.isFacilityInvoiceDisputeAuthorized}
          />
        )}
        {isWorkplace && (
          <PrivateRoute
            exact
            path={routes.timesheetRedirect}
            component={WorkplaceTimesheetViewRedirect}
            isAuthorized={workplaceMenuPrivileges.routeAuth?.hasDefaultWorkplacePermissions}
          />
        )}

        {/* ================================================================
            5. Worker routes — gated by isWorker
               Note: /agent/dashboard is handled in section 3 above
            ================================================================ */}
        {isWorker && (
          <PrivateRoute
            path={routes.chatWithConversation}
            component={RedesignedChatPage}
            isAuthorized={workerMenuPrivileges.chatRedesign}
            isAgent={shouldRedirectToMobileApp}
          />
        )}
        {isWorker && (
          <PrivateRoute
            path={routes.agentDnr}
            component={WorkerDnrPage}
            isAuthorized={workerMenuPrivileges.dnr}
            isAgent={shouldRedirectToMobileApp}
          />
        )}
        {isWorker && (
          <PrivateRoute
            path={routes.agentPayroll}
            component={Payroll}
            isAuthorized={workerMenuPrivileges.payrollSection?.payroll}
            isAgent={shouldRedirectToMobileApp}
          />
        )}
        {isWorker && (
          <PrivateRoute
            path={routes.agentStripeTransfers}
            component={StripeTransfers}
            isAuthorized={workerMenuPrivileges.payrollSection?.stripeTransfers}
            isAgent={shouldRedirectToMobileApp}
          />
        )}
        {isWorker && (
          <PrivateRoute
            path={routes.agentStripePayouts}
            component={StripePayouts}
            isAuthorized={workerMenuPrivileges.payrollSection?.stripePayouts}
            isAgent={shouldRedirectToMobileApp}
          />
        )}
        {isWorker && (
          <PrivateRoute
            path={routes.agentAttendance}
            component={AttendanceScore}
            isAuthorized={workerMenuPrivileges.clipboardScore}
            isAgent={shouldRedirectToMobileApp}
          />
        )}
        {isWorker && (
          <PrivateRoute
            path={routes.agentPlacement}
            component={AdminPlacementCandidatePage}
            isAuthorized={workerMenuPrivileges.placementSection?.candidateProfile}
            isAgent={shouldRedirectToMobileApp}
          />
        )}
        {isWorker && (
          <PrivateRoute
            path={routes.agentPlacementCandidateExclusions}
            component={AgentExclusionsPage}
            isAuthorized={workerMenuPrivileges.placementSection?.exclusions}
            isAgent={shouldRedirectToMobileApp}
          />
        )}
        {isWorker && (
          <PrivateRoute
            exact
            path={routes.agentProfile}
            component={WorkerEditProfile}
            isAuthorized={isWorkerSession}
            isAgent={shouldRedirectToMobileApp}
          />
        )}

        {/* ================================================================
            6. Admin routes — gated by admin
            ================================================================ */}
        {/* More-specific /admin/accounts/invoicing/* dispute routes must
            appear before the catch-all /admin/accounts route so that
            <Switch> matches them first. */}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminAccountsPostInvoiceDisputes}
            component={AdminPostInvoiceDisputes}
            isAuthorized={adminMenuPrivileges.routeAuth?.hasBillingOrAdministrativeRole}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminAccountsPreInvoiceDisputes}
            component={AdminPreInvoiceDisputes}
            isAuthorized={adminMenuPrivileges.routeAuth?.hasBillingOrAdministrativeRole}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminInvoiceDispute}
            component={AdminDisputePage}
            isAuthorized={adminMenuPrivileges.routeAuth?.hasBillingOrAdministrativeRole}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminPreInvoiceDispute}
            component={AdminPreInvoiceDisputeResolution}
            isAuthorized={adminMenuPrivileges.routeAuth?.hasBillingOrAdministrativeRole}
          />
        )}
        {admin && (
          <PrivateRoute
            path={[routes.adminSmsPortalConversation, routes.adminSmsPortal]}
            component={SmsPortalPage}
            isAuthorized={adminMenuPrivileges.hiringSection?.smsPortal}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminAccounts}
            component={Accounts}
            isAuthorized={adminMenuPrivileges.accountsSection}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminShiftVerificationFacilities}
            component={FacilityPreferencesPage}
            isAuthorized={adminMenuPrivileges.shiftsSection?.shiftsVerification}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminShiftVerificationShifts}
            component={ShiftVerificationPage}
            isAuthorized={adminMenuPrivileges.shiftsSection?.shiftsVerification}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminShiftVerificationShiftCreate}
            component={ShiftCreatePage}
            isAuthorized={adminMenuPrivileges.shiftsSection?.shiftsVerification}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminShiftVerificationExtras}
            component={ExtrasPage}
            isAuthorized={adminMenuPrivileges.shiftsSection?.shiftsVerification}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminShiftVerificationManual}
            component={ManualShiftVerificationPage}
            isAuthorized={adminMenuPrivileges.shiftsSection?.shiftsVerification}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminShiftVerification}
            component={ShiftVerificationLandingPage}
            isAuthorized={adminMenuPrivileges.shiftsSection?.shiftsVerification}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminShifts}
            component={ShiftSearchPage}
            isAuthorized={adminMenuPrivileges.shiftsSection?.shiftSearch}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminLogs}
            component={Logs}
            isAuthorized={adminMenuPrivileges.shiftsSection?.shiftLogs}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminTimeclock}
            component={TimeclockRequests}
            isAuthorized={adminMenuPrivileges.shiftsSection?.timeclockRequests}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminUrgentShiftNcns}
            component={UrgentShiftsNoShowCases}
            isAuthorized={adminMenuPrivileges.shiftsSection?.noShows}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.agentProof}
            component={ProofOfEarnings}
            isAuthorized={adminMenuPrivileges.payrollSection?.proofOfEarning}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminShiftPaymentsPayRates}
            component={PayRates}
            isAuthorized={adminMenuPrivileges.payrollSection?.shiftPayments}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminShiftPayments}
            component={ShiftPayments}
            isAuthorized={adminMenuPrivileges.routeAuth?.isAdmin}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminToolsTimekeepingVideos}
            component={AdminToolsTimekeepingVideosPage}
            isAuthorized={adminMenuPrivileges.adminToolsSection?.timekeepingVideos}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminToolsShiftExplainer}
            component={AdminToolsShiftExplainerPage}
            isAuthorized={adminMenuPrivileges.adminToolsSection?.shiftExplainer}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminHiringScraping}
            component={PlacementScrapingPage}
            isAuthorized={adminMenuPrivileges.hiringSection?.scraping}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminToolsBadgeVerificationGrading}
            component={BadgeVerificationGradingPage}
            isAuthorized={adminMenuPrivileges.adminToolsSection?.badgeVerificationGrading}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminToolsSsnWorkerLookup}
            component={SsnWorkerLookupPage}
            isAuthorized={adminMenuPrivileges.adminToolsSection?.ssnWorkerLookup}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminToolsEvidences}
            component={WorkerEvidencesPage}
            isAuthorized={adminMenuPrivileges.adminToolsSection?.workerEvidences}
          />
        )}
        {admin && (
          <PrivateRoute
            path={routes.adminToolsHomeHealthWorkerLookup}
            component={HomeHealthWorkerLookupPage}
            isAuthorized={adminMenuPrivileges.adminToolsSection?.homeHealthWorkerLookup}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminManageQualifications}
            component={ManageQualifications}
            isAuthorized={adminMenuPrivileges.staffingSection?.qualifications}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminManageWorkplaceTypes}
            component={ManageWorkplaceTypes}
            isAuthorized={adminMenuPrivileges.staffingSection?.workplaceTypes}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminPlacementsAvailability}
            component={AdminPlacementsAvailabilityPage}
            isAuthorized={adminMenuPrivileges.hiringSection?.availability}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminQRCode}
            component={AdminQRCode}
            isAuthorized={true}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminToolsEligibility}
            component={RequirementV2}
            isAuthorized={adminMenuPrivileges.adminToolsSection?.eligibility}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminDocument}
            component={AdminWorkerDocuments}
            isAuthorized={adminMenuPrivileges.accountsSection?.requirements}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.document}
            component={AdminDocuments}
            isAuthorized={adminMenuPrivileges.accountsSection?.requirements}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminInvoice}
            component={InvoiceView}
            isAuthorized={adminMenuPrivileges.routeAuth?.isAdmin}
          />
        )}
        {admin && (
          <PrivateRoute
            exact
            path={routes.adminInvoicePdf}
            component={AdminPDFGenerator}
            isAuthorized={adminMenuPrivileges.routeAuth?.isAdmin}
          />
        )}
        {admin && <Route exact path={routes.timesheet} component={AdminTimesheetView} />}
        {admin && (
          <PrivateRoute
            exact
            path={routes.agentICAgreement}
            component={DownloadICAgreement}
            isAuthorized={adminMenuPrivileges.routeAuth?.isAdmin}
          />
        )}

        {/* ================================================================
            7. Catch-all redirect — session-aware
            ================================================================ */}
        <Redirect to={catchAllRedirect} />
      </Switch>
    </Suspense>
  );
}
/* eslint-enable max-lines */
